/**
 * Resources Page
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { BookOpen, FileText, Video, Calculator, ArrowRight, Download, Clock } from "lucide-react";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663400393649/gEFtNmSeGBDwQH79ZrNHXT/resources_hero-J1aXyXS1HgUwxyxc2x5bJj.webp";

const categories = ["All", "Guides", "Templates", "Videos", "Tools"];

// Helper function for type badge styles
const getTypeBadgeStyles = (type) => {
  switch (type) {
    case "Guides":
      return "bg-blue-100 text-blue-700";
    case "Templates":
      return "bg-purple-100 text-purple-700";
    case "Videos":
      return "bg-red-100 text-red-700";
    default:
      return "bg-green-100 text-green-700";
  }
};

// Helper function for download button text
const getDownloadText = (downloadType) => {
  switch (downloadType) {
    case "Video":
      return "Watch";
    case "Tool":
      return "Use Tool";
    default:
      return "Download";
  }
};

// Helper function for download button icon
const getDownloadIcon = (downloadType) => {
  if (downloadType === "PDF" || downloadType === "DOCX") {
    return <Download size={14} />;
  }
  return <ArrowRight size={14} />;
};

const resources = [
  {
    id: 1, type: "Guides", icon: BookOpen, title: "Complete Guide to Business Credit",
    description: "Everything you need to know about building and leveraging business credit.",
    downloadType: "PDF", readTime: "15 min read", featured: true,
  },
  {
    id: 2, type: "Templates", icon: FileText, title: "Business Plan Template",
    description: "Professional template used by successful funding applications.",
    downloadType: "DOCX", readTime: "Template", featured: false,
  },
  {
    id: 3, type: "Guides", icon: BookOpen, title: "SBA Loan Application Checklist",
    description: "Step-by-step checklist for a successful SBA loan application.",
    downloadType: "PDF", readTime: "10 min read", featured: false,
  },
  {
    id: 4, type: "Videos", icon: Video, title: "Understanding Business Funding Options",
    description: "Video walkthrough of different funding types and when to use each.",
    downloadType: "Video", readTime: "12 min watch", featured: true,
  },
  {
    id: 5, type: "Tools", icon: Calculator, title: "Loan Calculator",
    description: "Calculate your monthly payments and total costs for any loan amount.",
    downloadType: "Tool", readTime: "Interactive", featured: false,
  },
  {
    id: 6, type: "Guides", icon: BookOpen, title: "Equipment Financing 101",
    description: "Learn how to finance equipment purchases effectively.",
    downloadType: "PDF", readTime: "8 min read", featured: false,
  },
];

export default function Resources() {
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = selectedCategory === "All" ? resources : resources.filter((r) => r.type === selectedCategory);

  return (
    <div className="min-h-screen bg-white" data-testid="resources-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden pt-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52]/95 via-[#0f2d52]/80 to-[#0f2d52]/30" />
        <div className="container relative z-10 py-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Resource Center</span>
            </div>
            <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl md:text-6xl font-black text-white leading-tight mb-5">
              Free Tools & <span className="text-[#48d480]">Resources</span>
            </h1>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
              Access our library of guides, templates, and tools to help your business succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-gray-50 border-b border-gray-100 sticky top-[80px] z-30">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2.5 rounded-full font-bold text-sm transition-all border-2 ${
                  selectedCategory === cat
                    ? "bg-[#0f2d52] text-white border-[#0f2d52]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#0f2d52]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((resource) => {
              const Icon = resource.icon;
              return (
                <div
                  key={resource.id}
                  className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all ${
                    resource.featured ? "border-[#48d480]" : "border-gray-200"
                  }`}
                >
                  {resource.featured && (
                    <div className="bg-[#48d480] text-[#0f2d52] text-xs font-bold px-4 py-1.5 text-center">
                      Featured Resource
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-[#0f2d52]/10 flex items-center justify-center">
                        <Icon size={22} className="text-[#0f2d52]" />
                      </div>
                      <span
                        className={`text-xs font-bold px-3 py-1 rounded-full ${getTypeBadgeStyles(resource.type)}`}
                      >
                        {resource.type}
                      </span>
                    </div>
                    <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-lg mb-2">{resource.title}</h3>
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm mb-4 leading-relaxed">{resource.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-gray-400 text-xs">
                        <Clock size={14} />
                        <span>{resource.readTime}</span>
                      </div>
                      <button className="flex items-center gap-1 text-[#48d480] font-bold text-sm hover:underline">
                        {getDownloadText(resource.downloadType)}
                        {getDownloadIcon(resource.downloadType)}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="container text-center max-w-2xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-4xl font-black text-white mb-4">
            Need Personalized <span className="text-[#48d480]">Guidance?</span>
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 mb-8">
            Schedule a free consultation with one of our funding specialists.
          </p>
          <Link to="/contact">
            <button className="btn-primary flex items-center gap-2 mx-auto">
              Book Free Consultation <ArrowRight size={16} />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
